import { window } from "../utils/window.util";
import { deiterateAPI } from "../utils/deiterateAPI.util";
import { sanitise } from "../utils/sanitise.util";
import { auth, db, FirebaseFieldValue, firebaseImport, FirebaseTimestamp, storageRef, storage, firebaseConfig } from "../../config/main_config";
import SupportingDocsModal from '../../../../htmlComponents/SupportingDocsModal.html?raw';
import { loadSelectorModal } from "../ControlSelector/controlSelector.module";
import { nameFromUID } from "../utils/nameFromUID.util";
import Swal from "sweetalert2";
import * as SparkMD5 from "spark-md5";
import { downloadFromSDK } from "../downloadFromSDK.module";
window.SparkMD5 = SparkMD5.default;

declare global {
    interface Date {
        addDays(days: number): Date;
    }

    interface String {
        toTitleCase(): string;
    }
}

// Implement the addDays method
Date.prototype.addDays = function (this: Date, days: number): Date {
    const date = new Date(this.valueOf());
    date.setDate(date.getDate() + days);
    return date;
};

String.prototype.toTitleCase = function () {
    return this.replace(/\b\w/g, function (char: string) {
        return char.toUpperCase();
    })
        .replace(/([A-Z])/g, ' $1')
        .trim();
};


const formatHistoryValue = (value: any): string => {
    if (value instanceof FirebaseTimestamp) {
        return value.toDate().toLocaleDateString();
    }
    if (value instanceof firebaseImport.firestore.GeoPoint) {
        return `${value.latitude}, ${value.longitude}`;
    }
    if (value instanceof Date) {
        return value.toLocaleDateString();
    }
    if (Array.isArray(value)) {
        return value.join(', ');
    }
    if (value === null) {
        return 'null';
    }
    if (value === undefined) {
        return 'undefined';
    }
    return value.toString();
};

/**
 * Decodes HTML entities in a given string.
 *
 * @param text - The encoded string containing HTML entities.
 * @returns The decoded string with HTML entities converted to their corresponding characters.
 */
function decodeHTMLEntities(text: string): string | null {
    const parser: DOMParser = new DOMParser();
    const doc: Document = parser.parseFromString(text, 'text/html');
    return doc.documentElement.textContent;
}


export namespace SupportingDocs {
    export enum DocType {
        Standard,
        Custom,
        Any,
    }

    export enum SupportedUpdateTypes {
        File,
        Confluence,
        EditOnly
    }

    export type CompanyID = string;
    export type DocumentReference = firebaseImport.firestore.DocumentReference;
    export type CollectionReference = firebaseImport.firestore.CollectionReference;

    export interface ConfluencePage {
        pageName: string;
        pageID: string;
        customerID: string;
        userID: string;
        lastReviewDate?: firebaseImport.firestore.Timestamp | Date | Object | null;
        nextReviewDate?: firebaseImport.firestore.Timestamp | Date | Object | null;
        docClassification?: string | null;
        docComments?: string | null;
        docProcRef?: string[] | null;
        confluenceURL?: string | null;
        edit: boolean;
        docID?: string | null;

    }

    export interface IDocumentData {
        classification?: string | null;
        comments?: string | null;
        confluenceURL?: string | null;
        dateUploaded?: firebaseImport.firestore.Timestamp | Date | Object | null;
        docHash?: string | null;
        docID?: string | null;
        documentType?: string | null;
        fileName?: string | null;
        displayName?: string | null;
        lastReviewDate?: firebaseImport.firestore.Timestamp | Date | Object | null;
        msgPages?: string[];
        nextReviewDate?: firebaseImport.firestore.Timestamp | Date | Object | null;
        procRef?: string[] | null;
        uploadedBy?: string | null;
        updateMethod?: string | null;
        updatedBy?: string | null;
        companyID: CompanyID;
        documentHistory?: string[] | null;
        componentId?: string | null;
        status?: string | null;
        [key: string]: any;
    }

    export class Page {
        public injectScripts(scripts: string | string[], moduleName): void {
            if (typeof scripts === "string") {
                // Check if module exists on window already
                if (window[moduleName] !== undefined) {
                    console.log(`Module already loaded: ${moduleName}`);
                    return;
                }

                // Check if script already exists in the document
                const existingScript = Array.from(document.getElementsByTagName('script')).find(
                    (script) => script.src === scripts
                );
                if (existingScript) {
                    console.log(`Script already loaded: ${scripts}`);
                    return;
                }

                // Create a new script element and set its source attribute to the provided URL or array of URLs
                var script = document.createElement('script');
                script.type = 'text/javascript';
                script.src = scripts;

                // Optional: Add onload event handler
                script.onload = function () {
                    console.log('Library has been loaded.', script);
                };

                // Optional: Add onerror event handler
                script.onerror = function () {
                    console.error('Failed to load library.', script);
                };

                // Append the script to the head or body
                document.head.appendChild(script);
            } else {
                scripts.forEach((scriptLink) => {
                    // Check if script already exists in the document
                    const existingScript = Array.from(document.getElementsByTagName('script')).find(
                        (script) => script.src === scriptLink
                    );
                    if (existingScript) {
                        console.log(`Script already loaded: ${scriptLink}`);
                        return;
                    }

                    // Create a new script element and set its source attribute to the provided URL or array of URLs
                    var script = document.createElement('script');
                    script.type = 'text/javascript';
                    script.src = scriptLink;

                    // Optional: Add onload event handler
                    script.onload = function () {
                        console.log('Library has been loaded.', script);
                    };

                    // Optional: Add onerror event handler
                    script.onerror = function () {
                        console.error('Failed to load library.', script);
                    };

                    // Append the script to the head or body
                    document.head.appendChild(script);
                });
            }
        }
    }

    export class Documents {
        private _documents: Document[] = [];
        private _companyID: CompanyID;

        public get documents(): Document[] {
            return this._documents;
        }

        public constructor(companyID: CompanyID | null | undefined = null) {
            if (companyID === null || companyID === undefined) {
                this._companyID = window.companyID;
            } else {
                this._companyID = companyID;
            }
            // new Page().injectScripts("https://cdnjs.cloudflare.com/ajax/libs/spark-md5/3.0.2/spark-md5.min.js");
            this.loadDocuments(true);
        }

        /**
         * Loads documents from Firestore if not already loaded.
         */
        public async loadDocuments(fresh: boolean = false): Promise<Document[]> {
            if (this._documents.length === 0 || fresh) {
                try {
                    const docRef = db.doc(`customers/${this._companyID}/product/policyPackages/ISO27001keyDocuments/otherDocs`);
                    const doc = await docRef.get();
                    if (doc.exists) {
                        const data = doc.data();
                        this._documents = Object.entries(data.uploaded || {})
                            .map(([docID, docData]: [string, any]) => {
                                const doc = new Document({ ...docData, docID, companyID: this._companyID });
                                return doc;
                            });
                    } else {
                        this._documents = [];
                    }
                } catch (error) {
                    console.error("Error loading documents:", error);
                    this._documents = [];
                }
            }
            return this._documents;
        }

        /**
         * Fetches all deleted document revisions from the database
         * @param companyID Optional company ID, defaults to current company
         * @returns Promise that resolves to an array of deleted document revisions
         */
        public static async getDeletedDocuments(companyID: string | null = window.companyID || null): Promise<Array<{
            id: string;
            data: any;
            deletedAt: any;
            deletedBy: string;
            originalDocId: string;
            originalFileName: string;
        }>> {
            try {
                const effectiveCompanyID = companyID || window.companyID;
                if (!effectiveCompanyID) {
                    throw new Error("No company ID provided and window.companyID is not set");
                }

                console.log(`[SupportingDocs] Fetching deleted documents for company: ${effectiveCompanyID}`);

                const deletedDocsRef = db.collection(`customers/${effectiveCompanyID}/documentRevisions`);
                const snapshot = await deletedDocsRef.get();

                if (snapshot.empty) {
                    console.log('[SupportingDocs] No deleted documents found');
                    return [];
                }

                const deletedDocs = snapshot.docs.map(doc => ({
                    id: doc.id,
                    data: doc.data(),
                    deletedAt: doc.data().deletedAt,
                    deletedBy: doc.data().deletedBy || 'Unknown',
                    originalDocId: doc.data().originalDocId || doc.data().docID || 'Unknown',
                    originalFileName: doc.data().originalFileName || doc.data().fileName || 'Unknown',
                }));

                console.log(`[SupportingDocs] Found ${deletedDocs.length} deleted documents`);
                return deletedDocs;
            } catch (error) {
                console.error('[SupportingDocs] Error fetching deleted documents:', error);
                throw new Error(`Failed to fetch deleted documents: ${error.message}`);
            }
        }
    }

    export class Document {
        public classification?: string | null;
        public comments?: string | null;
        public confluenceURL?: string | null;
        public dateUploaded?: firebaseImport.firestore.Timestamp | Date | Object | null;
        public docHash?: string | null;
        public docID?: string | null;
        public fileName?: string | null;
        public displayName?: string | null;
        public lastReviewDate?: firebaseImport.firestore.Timestamp | Date | Object | null;
        public msgPages?: string[] = [];
        public nextReviewDate?: firebaseImport.firestore.Timestamp | Date | Object | null;
        public procRef?: string[] | null;
        public assignedUsers?: string[] | null;
        public uploadedBy?: string | null;
        public updateMethod?: string | null;
        public updatedBy?: string | null;
        public status?: string | null;
        public documentType?: string | null;
        public componentId?: string | null;
        public version?: string | null;
        public linkedFromTaskId?: string | null;
        public companyID: CompanyID;
        [key: string]: any;

        private _originalData: IDocumentData;

        constructor(documentData: IDocumentData | null = null) {
            if (documentData !== null) {
                Object.assign(this, documentData);
                this._originalData = { ...documentData }; // Shallow copy for change detection
            } else {
                this._originalData = { companyID: (this.companyID === undefined ? window.companyID : this.companyID) };
            }
        }

        /**
         * Checks if the document has unsaved changes.
         */
        public get hasChanged(): boolean {
            return !this._isEqual(this._originalData, this.toData());
        }

        /**
         * Converts the document instance to a plain object suitable for Firestore.
         */
        public toData(): IDocumentData {
            return {
                assignedUsers: this.assignedUsers || [],
                classification: this.classification || 'Business Confidential',
                comments: this.comments || '',
                confluenceURL: this.confluenceURL || null,
                dateUploaded: this.dateUploaded || new Date(),
                docHash: this.docHash || '',
                docID: this.docID,
                fileName: this.fileName,
                displayName: this.displayName ?? null,
                lastReviewDate: this.lastReviewDate || new Date(),
                msgPages: this.msgPages || [],
                nextReviewDate: this.nextReviewDate || new Date(),
                procRef: this.procRef || [],
                uploadedBy: this.uploadedBy || auth.currentUser.uid || '',
                updateMethod: this.updateMethod || '',
                updatedBy: auth.currentUser.uid,
                companyID: (this.companyID === undefined ? window.companyID : this.companyID),
                documentType: this.documentType || "Policy",
                componentId: this.componentId || "SUP-" + this.docID,
                status: this.status || "Draft",
                version: this.version || '1.0',
                linkedFromTaskId: this.linkedFromTaskId || null
            };
        }

        /*
        * Checks if document has key
        */
        private _hasKey(key: string): boolean {
            return Object.prototype.hasOwnProperty.call(this, key);
        }

        /**
         * Updates the document in Firestore.
         */
        public async update(): Promise<void> {
            const updatedData = this.toData();
            console.log(updatedData)
            delete updatedData?.companyID;
            try {
                console.log(`db
                    .collection("customers")
                    .doc("${(this.companyID === undefined ? window.companyID : this.companyID)}")
                    .collection("product")
                    .doc("policyPackages")
                    .collection("ISO27001keyDocuments")
                    .doc("otherDocs")`);
                await db
                    .collection("customers")
                    .doc((this.companyID === undefined ? window.companyID : this.companyID))
                    .collection("product")
                    .doc("policyPackages")
                    .collection("ISO27001keyDocuments")
                    .doc("otherDocs")
                    .set({
                        uploaded: {
                            [this.docID]: updatedData,
                        },
                    }, { merge: true });

                // Remove the old document from the original data
                delete this._originalData?.[this.docID];
                // Update the original data after successful update
                this._originalData = { ...updatedData };
            } catch (error) {
                console.error("Error updating document: ", error);
                throw error;
            }
        }

        private async getFileHash(file: File): Promise<string> {
            return new Promise((resolve, reject) => {
                const blobSlice = File.prototype.slice || (File.prototype as any).mozSlice || (File.prototype as any).webkitSlice;
                let fileHash: string;

                if (!file) {
                    console.error("File not provided.");
                    reject(new Error("File not provided."));
                    return;
                }

                // Check if SparkMD5 library is available
                if (typeof window.SparkMD5 === "undefined") {
                    reject(new Error("SparkMD5 library is not available."));
                    return;
                }

                const chunkSize = 2 * 1024 * 1024; // 2MB chunks
                const chunks = Math.ceil(file.size / chunkSize);
                let currentChunk = 0;
                const spark = new SparkMD5.ArrayBuffer();
                const fileReader = new FileReader();

                fileReader.onload = function (e: ProgressEvent<FileReader>) {
                    console.log("Loading chunk...", currentChunk + 1, "of", chunks);
                    spark.append(e.target!.result as ArrayBuffer); // Append array buffer
                    currentChunk++;
                    if (currentChunk < chunks) {
                        loadNext();
                    } else {
                        console.log("Finished loading all chunks. Hashing...");
                        fileHash = spark.end();
                        console.log("File hash:", fileHash);
                        resolve(fileHash);
                    }
                };

                fileReader.onerror = function (e) {
                    console.error("File reading error: ", e);
                    reject(e);
                };

                function loadNext() {
                    const start = currentChunk * chunkSize;
                    const end = Math.min(start + chunkSize, file.size);
                    const slice = blobSlice.call(file, start, end);
                    fileReader.readAsArrayBuffer(slice);
                }

                loadNext(); // Start reading the first chunk
            });
        }

        private _validateProperty(property: string, allowEmpty: boolean = false): boolean {
            if (!this._hasKey(property)) {
                console.error(new Error(`Property "${property}" does not exist.`));
                return false;
            }

            const value = this[property];

            if (value === undefined || value === null) {
                console.error(new Error(`Property "${property}" is required.`));
                return false;
            }

            if (!allowEmpty && value === "") {
                console.error(new Error(`Property "${property}" must not be empty.`));
                return false;
            }

            return true;
        }

        private _prepare(file: File = null): Object {
            let fileData = {};
            console.log(this._validateProperty("fileName"))
            if (this._validateProperty("fileName", true)) {
                fileData["fileName"] = sanitise(file !== null ? file.name : this?.fileName);
            } else {
                fileData["fileName"] = sanitise(this?.originalFileName);
                // throw new Error("File name is required.");
            }

            if (this.displayName !== undefined) {
                fileData["displayName"] = this.displayName;
            }

            if (this._validateProperty("classification")) {
                fileData["docClassification"] = sanitise(this?.classification);
            } else {
                throw new Error("Classification is required.");
            }

            if (this._validateProperty("comments", true)) {
                fileData["docComments"] = sanitise(this?.comments);
            } else {
                fileData["docComments"] = "";
            }

            if (this._validateProperty("procRef", true)) {
                fileData["docProcRefs"] = this?.procRef;
            } else {
                fileData["docProcRefs"] = [];
            }

            if (this._validateProperty("nextReviewDate")) {
                fileData["nextReviewDate"] = this?.nextReviewDate instanceof FirebaseTimestamp
                    ? this?.nextReviewDate instanceof firebaseImport.firestore.Timestamp
                        ? this?.nextReviewDate.toDate()
                        : this?.nextReviewDate instanceof Date
                            ? this?.nextReviewDate
                            : new Date()
                    : this?.nextReviewDate instanceof Date
                        ? this?.nextReviewDate
                        : new Date();
            } else {
                fileData["nextReviewDate"] = new Date();
            }

            if (this._validateProperty("edit")) {
                fileData["edit"] = this?.edit;
                fileData["docID"] = this?.docID;
            }

            if (this._validateProperty("docHash")) {
                fileData["docHash"] = this?.docHash;
            }

            fileData["customerID"] = window.companyID;
            fileData["userID"] = this?.userID !== undefined ? this?.userID : auth.currentUser.uid;
            fileData["lastReviewDate"] = this?.lastReviewDate instanceof FirebaseTimestamp
                ? this?.lastReviewDate instanceof firebaseImport.firestore.Timestamp
                    ? this?.lastReviewDate.toDate()
                    : this?.lastReviewDate instanceof Date
                        ? this?.lastReviewDate
                        : new Date()
                : this?.lastReviewDate instanceof Date
                    ? this?.lastReviewDate
                    : new Date();

            fileData["msgPages"] = this?.msgPages !== undefined && this?.msgPages.length > 0 ? [...this?.msgPages.filter(route => route != window.location.hash.slice(2)), window.location.hash.slice(2)] : [window.location.hash.slice(2)];
            fileData["documentType"] = this?.documentType || '';
            fileData["componentId"] = this?.componentId || '';
            fileData["status"] = this?.status || 'current';
            fileData["businessUnit"] = this?.businessUnit || '';
            fileData["linkedFromTaskId"] = this?.linkedFromTaskId || '';

            return fileData;
        }

        private _prepareConfluence(): Object {
            let fileData = {};
            console.log(this._validateProperty("pageName"))
            if (this._validateProperty("pageName")) {
                fileData["pageName"] = this?.pageName;
            } else {
                throw new Error("Page name is required.");
            }

            if (this._validateProperty("pageID")) {
                fileData["pageID"] = this?.pageID;
            } else {
                throw new Error("Page name is required.");
            }

            if (this._validateProperty("confluenceURL")) {
                fileData["confluenceURL"] = this?.pageID;
            } else {
                throw new Error("Page name is required.");
            }

            if (this._validateProperty("classification")) {
                fileData["docClassification"] = sanitise(this?.classification);
            } else {
                throw new Error("Classification is required.");
            }

            if (this._validateProperty("comments", true)) {
                fileData["docComments"] = sanitise(this?.comments);
            } else {
                fileData["docComments"] = "";
            }

            if (this._validateProperty("procRef", true)) {
                fileData["docProcRefs"] = this?.procRef;
            } else {
                fileData["docProcRefs"] = [];
            }

            if (this._validateProperty("nextReviewDate")) {
                fileData["nextReviewDate"] = this?.nextReviewDate instanceof FirebaseTimestamp
                    ? this?.nextReviewDate instanceof firebaseImport.firestore.Timestamp
                        ? this?.nextReviewDate.toDate()
                        : this?.nextReviewDate instanceof Date
                            ? this?.nextReviewDate
                            : new Date()
                    : this?.nextReviewDate instanceof Date
                        ? this?.nextReviewDate
                        : new Date();
            } else {
                fileData["nextReviewDate"] = new Date();
            }

            if (this._validateProperty("edit")) {
                fileData["edit"] = this?.edit;
                fileData["docID"] = this?.docID;
            }

            fileData["customerID"] = window.companyID;
            fileData["userID"] = this?.userID !== undefined ? this?.userID : auth.currentUser.uid;
            fileData["lastReviewDate"] = this?.lastReviewDate instanceof FirebaseTimestamp
                ? this?.lastReviewDate instanceof firebaseImport.firestore.Timestamp
                    ? this?.lastReviewDate.toDate()
                    : this?.lastReviewDate instanceof Date
                        ? this?.lastReviewDate
                        : new Date()
                : this?.lastReviewDate instanceof Date
                    ? this?.lastReviewDate
                    : new Date();
            fileData["msgPages"] = this?.msgPages !== undefined && this?.msgPages.length > 0 ? [...this?.msgPages.filter(route => route != window.location.hash.slice(2)), window.location.hash.slice(2)] : [window.location.hash.slice(2)];
            fileData["documentType"] = this?.documentType || '';
            fileData["componentId"] = this?.componentId || '';
            fileData["status"] = this?.status || 'current';

            return fileData;
        }

        private async _handleConfluenceFiles() {
            try {
                await deiterateAPI.POST("/deiterateInternalAPI/saveSupportingDoc", {
                    mode: 'cors',
                    body: JSON.stringify({
                        callType: 'confluence',
                        confluenceData: this._prepareConfluence(),
                    })
                })
            } catch (error) {
                throw new Error("Error handling Confluence files: ", error);
            }
        }

        /* Upload File to Firebase Storage and Firestore */
        public async upload(
            type: String = "file",
            file: File | ConfluencePage | object | null = null,
            newDocID: string | number | null = null,
            createMode: boolean = false,
        ): Promise<void> {

            if (type === "confluence") {
                return this._handleConfluenceFiles();
            }

            if (this._validateProperty("docID") && (file instanceof File || file === null) === true) {
                return this.replace(file instanceof File ? file : null, newDocID, createMode);
            } else {
                console.log("No DocID");
            }

        }

        /*
        * Replace file in firebase storage
        * and update the document in Firestore.
        */
        public async replace(
            file: File | null,
            newDocID: string | number | null = null,
            createMode: boolean = false,
        ): Promise<void> {
            let docID: string | number | null = this?.docID ? this?.docID : null;

            if (newDocID !== null) {
                docID = newDocID;
            }

            if (!docID) {
                throw new Error("Cannot replace file without a docID.");
            }

            if (file !== null) {
                let fileUploadReference = storageRef.child(
                    `/customers/${window.companyID}/keyDocuments/otherDocs/uploaded/${this?.docID}/${sanitise(file?.name)}`
                )

                try {
                    await fileUploadReference.put(file);
                } catch (error) {
                    throw new Error(`Failed to replace ${this?.fileName}.`, error)
                }

                const fileHash = await this.getFileHash(file);

                if (fileHash === undefined || fileHash === null || fileHash === "") {
                    throw new Error("Failed to get file hash or invalid file type.");
                }

                this.docHash = fileHash;
            }

            if (!createMode) {
                return await deiterateAPI.POST(`/deiterateInternalAPI/saveSupportingDoc`, {
                    mode: 'cors',
                    body: JSON.stringify({
                        callType: 'file',
                        manualFileData: this._prepare(file),
                    })
                }).then(response => {
                    return response;
                })
            } else {
                return new Promise<void>((resolve, reject) => {
                    resolve();
                })
            }
        }

        /*
        * Download the document file
        * Handles both file downloads and Confluence URL opening
        */
        public async download(): Promise<void> {
            return new Promise<void>((resolve, reject) => {
                if (!this.docID) {
                    reject(new Error("Document ID is required for download"));
                    return;
                }

                // For Confluence documents, just open the URL
                if (this.confluenceURL && this.confluenceURL.trim() !== '') {
                    window.open(this.confluenceURL, '_blank');
                    resolve();
                    return;
                }

                // Must have a filename for file-based documents
                if (!this.fileName || this.fileName.trim() === '') {
                    reject(new Error("Filename is required for file download"));
                    return;
                }

                try {
                    // Construct the storage path
                    const storagePath = `customers/${(this.companyID === undefined ? window.companyID : this.companyID)}/keyDocuments/otherDocs/uploaded/${this.docID}/${this.fileName}`;
                    console.log('Storage path:', storagePath);

                    // Use downloadFromSDK to handle the download
                    downloadFromSDK(
                        firebaseConfig,
                        storageRef,
                        'download',
                        null,
                        this.fileName,
                        storagePath
                    ).then(() => {
                        resolve();
                    }).catch((error) => {
                        console.error("Error during download:", error);
                        reject(error);
                    });
                } catch (error) {
                    console.error("Error initiating download:", error);
                    reject(error);
                }
            });
        }

        /*
        * Delete File
        */
        /**
         * Creates a revision of the current document before deletion
         * @private
         */
        private async _createRevision(): Promise<string> {
            if (!this.docID) {
                throw new Error("Cannot create revision without a docID.");
            }

            try {
                // Create a copy of the document data for the revision
                const revisionData = {
                    ...this.toData(),
                    deletedAt: FirebaseFieldValue.serverTimestamp(),
                    deletedBy: auth.currentUser?.uid || 'system',
                    originalDocId: this.docID,
                    originalFileName: this.fileName,
                    originalPath: `customers/${this.companyID}/product/policyPackages/ISO27001keyDocuments/otherDocs/uploaded/${this.docID}/${this.fileName}`
                };

                // Store the revision in a separate collection
                const revisionRef = db.collection(`customers/${this.companyID}/documentRevisions`).doc();
                await revisionRef.set(revisionData);

                // Copy the file to a backup location in storage if it exists
                if (this.fileName && !this.confluenceURL) {
                    const sourceRef = storageRef.child(`/customers/${this.companyID}/keyDocuments/otherDocs/uploaded/${this.docID}/${this.fileName}`);
                    const backupRef = storageRef.child(`/customers/${this.companyID}/documentRevisions/${revisionRef.id}/${this.fileName}`);

                    try {
                        const fileUrl = await sourceRef.getDownloadURL();
                        const response = await fetch(fileUrl);
                        const blob = await response.blob();
                        await backupRef.put(blob);
                    } catch (error) {
                        console.warn("Could not backup file to revision:", error);
                        // Continue with deletion even if backup fails
                    }
                }

                return revisionRef.id;
            } catch (error) {
                console.error("Failed to create document revision:", error);
                throw new Error("Failed to create document revision before deletion.");
            }
        }

        /**
         * Deletes the document after creating a revision
         */
        public async delete(): Promise<string> {
            try {
                if (!this._validateProperty("docID")) {
                    throw new Error("Cannot delete file without a docID.");
                }

                // Create a revision before deletion
                const revisionId = await this._createRevision();

                const documentReference = db.doc(`customers/${window.companyID}/product/policyPackages/ISO27001keyDocuments/otherDocs/`);
                const storageReference = this.fileName
                    ? storageRef.child(`/customers/${window.companyID}/keyDocuments/otherDocs/uploaded/${this.docID}/${this.fileName}`)
                    : null;

                // Get the current document data
                const docSnapshot = await documentReference.get();
                const docData = docSnapshot.data();

                if (!docData || !docData.uploaded) {
                    throw new Error("Document or uploaded data not found");
                }

                // Create a new object without the document we want to delete
                const updatedUploaded = { ...docData.uploaded };
                delete updatedUploaded[this.docID];

                // Update the document with the remaining documents
                await documentReference.set({
                    uploaded: updatedUploaded
                }).catch(error => {
                    throw new Error(`Failed to delete ${this.fileName} document: ${error.message}`);
                });

                // Delete the file from storage if it exists
                if (storageReference) {
                    await storageReference.delete().catch(error => {
                        console.warn(`Failed to delete ${this.fileName} from storage:`, error);
                        // Don't throw here as the document was already deleted from Firestore
                    });
                }

                return revisionId;
            } catch (error) {
                console.error("Failed to delete document:", error);
                throw new Error(`Failed to delete file: ${error.message}`);
            }
        }

        /**
         * Recovers a deleted document from a revision
         * @static
         * @param revisionId The ID of the revision to recover
         * @param newDocId Optional new document ID (if not provided, will use original)
         */
        public static async recover(revisionId: string, newDocId: string | null = null): Promise<Document> {
            try {
                const companyID = window.companyID;
                const revisionRef = db.doc(`customers/${companyID}/documentRevisions/${revisionId}`);
                const revisionDoc = await revisionRef.get();

                if (!revisionDoc.exists) {
                    throw new Error("Revision not found");
                }

                const revisionData = revisionDoc.data() as IDocumentData & {
                    originalDocId: string;
                    originalFileName: string;
                    originalPath: string;
                    deletedAt: any;
                    deletedBy: string;
                };

                // Prepare document data for recovery
                const { originalDocId, originalFileName, originalPath, deletedAt, deletedBy, ...documentData } = revisionData;
                const docId = newDocId || originalDocId;

                // Restore the document in Firestore
                const documentReference = db.doc(`customers/${companyID}/product/policyPackages/ISO27001keyDocuments/otherDocs/`);
                await documentReference.set({
                    uploaded: {
                        [docId]: {
                            ...documentData,
                            docID: docId,
                            dateUploaded: FirebaseFieldValue.serverTimestamp(),
                            updatedBy: auth.currentUser?.uid || 'system'
                        }
                    }
                }, { merge: true }).catch(error => {
                    console.error("Failed to recover document:", error);
                    throw new Error(`Failed to recover document: ${error.message}`);
                });

                // Restore the file from backup storage if it exists
                if (documentData.fileName && !documentData.confluenceURL) {
                    const sourceRef = storageRef.child(`/customers/${companyID}/documentRevisions/${revisionId}/${documentData.fileName}`);
                    const targetRef = storageRef.child(`/customers/${companyID}/keyDocuments/otherDocs/uploaded/${docId}/${documentData.fileName}`);

                    try {
                        const fileUrl = await sourceRef.getDownloadURL();
                        const response = await fetch(fileUrl);
                        const blob = await response.blob();
                        await targetRef.put(blob);
                    } catch (error) {
                        console.warn("Could not restore file from revision:", error);
                        // Continue with recovery even if file restore fails
                    }
                }

                // Optionally, you might want to delete the revision after successful recovery
                // await revisionRef.delete();


                return new Document({
                    ...documentData,
                    docID: docId,
                    companyID
                });
            } catch (error) {
                console.error("Failed to recover document:", error);
                throw new Error(`Failed to recover document: ${error.message}`);
            }
        }

        /**
         * Helper method to compare two objects.
         */
        private _isEqual(obj1: any, obj2: any): boolean {
            return JSON.stringify(obj1) === JSON.stringify(obj2);
        }

        /**
         * Creates a Document instance from Firestore data.
         */
        public static fromData(documentData: IDocumentData): Document {
            return new Document(documentData);
        }

        /**
         * Creates a Document instance from a Firestore snapshot.
         */
        public static fromSnapshot(
            snapshot: firebaseImport.firestore.DocumentSnapshot
        ): Document {
            if (!snapshot.exists) {
                return null;
            }
            return new Document(snapshot.data() as IDocumentData);
        }

        /**
         * Get the next available document ID
         * @param companyID Optional company ID, defaults to current company
         * @returns Promise resolving to the next available document ID as a number
         */
        public static async getNextAvailableDocID(companyID: CompanyID | null = null): Promise<number> {
            // Set default company ID if not provided
            if (companyID === null || companyID === undefined) {
                companyID = window.companyID;
            }

            let highestID = 0;

            try {
                // Query Firestore directly to get the raw document data
                const documentsReference = db
                    .collection("customers")
                    .doc(companyID)
                    .collection("product")
                    .doc("policyPackages")
                    .collection("ISO27001keyDocuments")
                    .doc("otherDocs");

                const doc = await documentsReference.get();

                if (doc.exists) {
                    const data = doc.data() as any;
                    const files = data.uploaded || {};

                    // Also check the doc_count value, which tracks the total documents
                    if (data.doc_count !== undefined && data.doc_count !== null) {
                        const docCount = typeof data.doc_count === 'number'
                            ? data.doc_count
                            : parseInt(String(data.doc_count), 10);

                        if (!isNaN(docCount) && docCount > highestID) {
                            console.log(`[SupportingDocs] Using doc_count as minimum: ${docCount}`);
                            highestID = docCount;
                        }
                    }

                    // Check numeric keys in the uploaded object
                    for (const key in files) {
                        if (files.hasOwnProperty(key)) {
                            const keyAsNumber = parseInt(key, 10);
                            if (!isNaN(keyAsNumber) && keyAsNumber > highestID) {
                                highestID = keyAsNumber;
                            }

                            // Also check the docID field inside each document
                            const docIdValue = files[key].docID;
                            if (docIdValue !== undefined && docIdValue !== null) {
                                const docIdAsNumber = typeof docIdValue === 'number'
                                    ? docIdValue
                                    : parseInt(String(docIdValue), 10);

                                if (!isNaN(docIdAsNumber) && docIdAsNumber > highestID) {
                                    highestID = docIdAsNumber;
                                }
                            }
                        }
                    }
                }

                // Always use highest ID + 1 to ensure no conflicts
                const nextID = highestID + 1;
                console.log(`[SupportingDocs] Using next ID: ${nextID} (highest found: ${highestID})`);
                return nextID;
            } catch (error) {
                console.error("Error getting highest document ID: ", error);
                // If there's an error, return a safe default (at least 1)
                return Math.max(1, highestID + 1);
            }
        }
    }

    export class Modal {
        public currentModalID: string;

        public async open(type: string = "new", doc: Document | string | number | null = null, findDoc: boolean = false): Promise<void | Error> {
            let ModalImplementation = this;
            new Page().injectScripts("https://cdnjs.cloudflare.com/ajax/libs/spark-md5/3.0.2/spark-md5.min.js", "SparkMD5");
            // Implement modal logic here
            if ((typeof doc === "string" || typeof doc === "number") && findDoc === true) {
                // Check all documents for a doc with the docID and open the modal
                let supportingDocs = new Documents(window.companyID);
                let documents = await supportingDocs.loadDocuments(true);
                for (let i = 0; i < documents.length; i++) {
                    if (documents[i].docID === doc) {
                        doc = documents[i] as Document;
                    }
                }
            } else if (!(doc instanceof Document) && doc !== null) {
                console.error(new Error("Invalid document data."));
                return;
            }
            console.log(doc)
            if (doc instanceof Document || doc === null) {
                if (document.querySelector("#SupportingDocsModal") as HTMLElement !== null) {
                    var modal = document.querySelector("#SupportingDocsModal") as HTMLElement;
                    if (type === "edit") {
                        if (doc === null && !(doc instanceof Document)) {
                            return new Error("Cannot edit document without a document.");
                        }

                        (modal.querySelector("#historyBtn") as HTMLElement).style.display = 'inline';

                        if (doc instanceof Document) {
                            // Populate modal with document data
                            modal.id = `supportingDocs${doc.docID}`;
                            this.currentModalID = modal.id;

                            const rootCustomerDoc = await db.collection('customers').doc(window.companyID).get();
                            if (rootCustomerDoc.data()?.customerPlan !== undefined) {
                                (modal.querySelector('#dataClassificationDiv') as HTMLElement).style.display = 'none';
                            }

                            window.supportingDocsModalID = `supportingDoc${doc.docID}`;
                            modal.setAttribute('data-docID', `${doc.docID}`);
                            modal.style.display = 'flex';
                            (modal.querySelector('#fileSelector') as HTMLElement).style.display = 'none';
                            (modal.querySelector('#editFileSelector') as HTMLElement).style.display = 'flex';
                            (modal.querySelector('#deleteBtn') as HTMLElement).style.display = 'flex';
                            (modal.querySelector('#sideModalHeader') as HTMLElement).innerText = 'Edit Supporting Document';
                            const lastReviewDateInput = modal.querySelector('#lastReviewDate');
                            const nextReviewDateInput = modal.querySelector('#nextReviewDate');
                            console.log(doc.lastReviewDate);
                            (lastReviewDateInput as HTMLInputElement).valueAsDate = doc.lastReviewDate instanceof FirebaseTimestamp
                                ? doc.lastReviewDate instanceof firebaseImport.firestore.Timestamp
                                    ? doc.lastReviewDate.toDate()
                                    : doc.lastReviewDate instanceof Date
                                        ? doc.lastReviewDate
                                        : new Date()
                                : doc.lastReviewDate instanceof Date
                                    ? doc.lastReviewDate
                                    : new Date();
                            (nextReviewDateInput as HTMLInputElement).valueAsDate = doc.nextReviewDate instanceof FirebaseTimestamp
                                ? doc.nextReviewDate instanceof firebaseImport.firestore.Timestamp
                                    ? doc.nextReviewDate.toDate()
                                    : doc.nextReviewDate instanceof Date
                                        ? doc.nextReviewDate
                                        : new Date()
                                : doc.nextReviewDate instanceof Date
                                    ? doc.nextReviewDate
                                    : new Date();

                            if (
                                doc?.confluenceURL !== undefined &&
                                doc?.confluenceURL !== ''
                            ) {
                                (modal.querySelector('#fileSourceSelectEdit') as HTMLInputElement).value = 'confluence';
                                (modal.querySelector('#confluenceFileUploadEdit') as HTMLInputElement).style.display = 'flex';
                                (modal.querySelector('#manualFileUploadDivEdit') as HTMLInputElement).style.display = 'none';
                                (modal.querySelector('#pageIDEdit') as HTMLInputElement).value = doc.confluenceURL;
                                (modal.querySelector('#pageNameEdit') as HTMLInputElement).value = doc.fileName.replace('.pdf', '');
                            } else {
                                (modal.querySelector('#fileSourceSelectEdit') as HTMLInputElement).value = 'file';
                                (modal.querySelector('#fileSourceSelectEdit') as HTMLInputElement).value = 'file';
                                (modal.querySelector('#confluenceFileUploadEdit') as HTMLInputElement).style.display = 'none';
                                (modal.querySelector('#manualFileUploadDivEdit') as HTMLInputElement).style.display = 'flex';
                                (modal.querySelector('#uploadTextEdit') as HTMLElement).innerText = 'Replace File';
                            }
                            if (doc?.stickyDoc === true) {
                                (modal.querySelector('#deleteBtn') as HTMLElement).style.display = 'none';
                            } else {
                                (modal.querySelector('#deleteBtn') as HTMLElement).style.display = 'flex';
                            }
                            (modal.querySelector('#fileComment') as HTMLInputElement).value = decodeHTMLEntities(doc.comments);
                            (modal.querySelector('#controlsSelectedInputEdit') as HTMLInputElement).value = doc.procRef.join(', ');
                            window.selectedControls = doc.procRef;
                            window.acceptedKeyFilters = [];
                            console.log(window.selectedControls);

                            const ownerSelect = modal.querySelector('#ownerSelect');
                            ownerSelect.innerHTML = '';
                            modal.querySelector('#classificationSelect').innerHTML = '';
                            const complianceOptions = await db
                                .collection('customers')
                                .doc(window.companyID)
                                .collection('compliance')
                                .doc('asset')
                                .get();

                            if (complianceOptions.data()?.register?.menu?.classification !== undefined) {
                                complianceOptions.data()?.register?.menu?.classification?.forEach((optionValue: string) => {
                                    const option = document.createElement('option');
                                    option.innerHTML = optionValue;
                                    option.value = optionValue;
                                    modal.querySelector('#classificationSelect')?.appendChild(option);
                                });
                            } else {
                                let classification = ['Customer Confidential', 'Business Confidential', 'Public'];
                                classification.forEach((optionValue) => {
                                    const option = document.createElement('option');
                                    option.innerHTML = optionValue;
                                    option.value = optionValue;
                                    modal.querySelector('#classificationSelect')?.appendChild(option);
                                });
                            }
                            (modal.querySelector('#classificationSelect') as HTMLInputElement).value = doc.classification;

                            await db
                                .collection('customers')
                                .doc(window.companyID)
                                .get()
                                .then(async (companyDoc) => {
                                    if (companyDoc.exists) {
                                        const customerUsers = Object.keys(companyDoc.data().customerOrg.users).sort();
                                        for (const user of customerUsers) {
                                            let usersUID = companyDoc.data().customerOrg.users[user].userID;
                                            let name = await nameFromUID(usersUID);
                                            let ownerOption = document.createElement('option');
                                            ownerOption.id = usersUID;
                                            ownerOption.value = usersUID;
                                            ownerOption.innerText = name;
                                            ownerSelect.appendChild(ownerOption);
                                        }
                                        (modal.querySelector('#ownerSelect') as HTMLSelectElement).value = doc instanceof Document ? doc.uploadedBy : '';
                                    }
                                });

                            (document.querySelector(`#${this.currentModalID}`) as HTMLElement).style.display = "block";

                            modal
                                .querySelector('#fileSourceSelect')
                                .addEventListener('change', (e) => {
                                    if ((e.target as HTMLInputElement).value === 'file') {
                                        (modal.querySelector('#manualFileUploadDiv') as HTMLElement).style.display = 'flex';
                                        (modal.querySelector('#confluenceFileUpload') as HTMLInputElement).style.display = 'none';
                                    } else if ((e.target as HTMLInputElement).value === 'confluence') {
                                        (modal.querySelector('#confluenceFileUpload') as HTMLInputElement).style.display = 'flex';
                                        (modal.querySelector('#manualFileUploadDiv') as HTMLElement).style.display = 'none';
                                    }
                                });

                            modal
                                .querySelector('#fileSourceSelectEdit')
                                .addEventListener('change', (e) => {
                                    if ((e.target as HTMLInputElement).value === 'file') {
                                        (modal.querySelector('#manualFileUploadDivEdit') as HTMLElement).style.display =
                                            'flex';
                                        (modal.querySelector('#confluenceFileUploadEdit') as HTMLElement).style.display =
                                            'none';
                                    } else if ((e.target as HTMLInputElement).value === 'confluence') {
                                        (modal.querySelector('#confluenceFileUploadEdit') as HTMLElement).style.display =
                                            'flex';
                                        (modal.querySelector('#manualFileUploadDivEdit') as HTMLElement).style.display =
                                            'none';
                                    }
                                });

                            modal.querySelector('#pageID').addEventListener('change', (e) => {
                                if (!(e.target as HTMLInputElement).value.includes('pages')) {
                                    modal.querySelector('.pageUrlDiv').classList.add('incorrectFormat');
                                    (modal.querySelector('#warningIcon') as HTMLElement).style.display = 'flex';
                                    (modal.querySelector('#save_file_button') as HTMLInputElement).disabled = true;
                                } else {
                                    modal.querySelector('.pageUrlDiv').classList.remove('incorrectFormat');
                                    (modal.querySelector('#warningIcon') as HTMLElement).style.display = 'none';
                                    (modal.querySelector('#save_file_button') as HTMLInputElement).disabled = false;
                                }
                            });

                            modal
                                .querySelector('#fileUploadSelector')
                                .addEventListener('click', (e) => {
                                    (modal.querySelector('#fileInput') as HTMLInputElement).click();
                                });

                            modal
                                .querySelector('#fileUploadSelectorEdit')
                                .addEventListener('click', (e) => {
                                    (modal.querySelector('#fileInputEdit') as HTMLInputElement).click();
                                });

                            modal.querySelector('#fileInput').addEventListener('change', (e) => {
                                if ((e.target as HTMLInputElement).files.length > 0) {
                                    (modal.querySelector('#uploadText') as HTMLElement).innerText = sanitise((e.target as HTMLInputElement).files[0].name);
                                    (modal.querySelector('#removeIcon') as HTMLElement).style.display = 'flex';
                                    (modal.querySelector('#uploadIcon') as HTMLElement).style.display = 'none';
                                } else {
                                    (modal.querySelector('#uploadText') as HTMLElement).innerText = 'Click to upload a file';
                                    (modal.querySelector('#removeIcon') as HTMLElement).style.display = 'none';
                                    (modal.querySelector('#uploadIcon') as HTMLElement).style.display = 'flex';
                                }
                            });

                            modal.querySelector('#fileInputEdit').addEventListener('change', (e) => {
                                if ((e.target as HTMLInputElement).files.length > 0) {
                                    (modal.querySelector('#uploadTextEdit') as HTMLElement).innerText = sanitise((e.target as HTMLInputElement).files[0].name);
                                    (modal.querySelector('#removeIconEdit') as HTMLElement).style.display = 'flex';
                                    (modal.querySelector('#uploadIconEdit') as HTMLElement).style.display = 'none';
                                } else {
                                    (modal.querySelector('#uploadTextEdit') as HTMLElement).innerText = 'Click to upload a file';
                                    (modal.querySelector('#removeIconEdit') as HTMLElement).style.display = 'none';
                                    (modal.querySelector('#uploadIconEdit') as HTMLElement).style.display = 'flex';
                                }
                            });

                            (modal.querySelector('#removeIcon') as HTMLInputElement).addEventListener('click', () => {
                                (modal.querySelector('#fileInput') as HTMLInputElement).files = null;
                            });

                            modal.querySelector('#deleteBtn').addEventListener('click', async (e) => {
                                await db
                                    .doc(
                                        `/customers/${window.companyID}/product/policyPackages/ISO27001keyDocuments/otherDocs`,
                                    )
                                    .get()
                                    .then(async (otherDocs): Promise<void | boolean> => {
                                        if (otherDocs.exists) {
                                            let docID = parseInt(modal.getAttribute('data-docID'));
                                            (e.target as HTMLElement).innerHTML = `<i class="fa fa-spinner fa-spin"></i>`;
                                            const storageDocs = storageRef.child(
                                                `/customers/${window.companyID}/keyDocuments/otherDocs/uploaded/${docID}/${otherDocs.data().uploaded[docID].fileName}`,
                                            );
                                            try {
                                                await storageDocs.delete();
                                            } catch (error) {
                                                console.error('DELETE FILE ERROR', error);
                                            }
                                            return await db
                                                .collection('customers')
                                                .doc(window.companyID)
                                                .collection('product')
                                                .doc('policyPackages')
                                                .collection('ISO27001keyDocuments')
                                                .doc('otherDocs')
                                                .set(
                                                    {
                                                        uploaded: {
                                                            [docID]: FirebaseFieldValue.delete(),
                                                        },
                                                    },
                                                    { merge: true },
                                                )
                                                .then(async (): Promise<void | boolean> => {
                                                    (e.target as HTMLElement).innerText = `Delete`;
                                                    modal.style.display = 'none';
                                                    modal.remove();
                                                    let docsTable = document.getElementById('docsTable');
                                                    docsTable?.remove();
                                                    return await db.collection('customers')
                                                        .doc(window.companyID)
                                                        .get()
                                                        .then((customerDocument) => {
                                                            if (customerDocument.exists) {
                                                                return true;
                                                            } else {
                                                                return false;
                                                            }
                                                        });
                                                })
                                                .catch((error) => {
                                                    console.log(error);
                                                });
                                        }
                                    });
                            });

                            modal
                                .querySelector('#save_file_button')
                                .addEventListener('click', async (e) => {
                                    (e.target as HTMLElement).innerHTML = `<i class="fa fa-spinner fa-spin"></i>`;

                                    if (
                                        (modal.querySelector('#editFileSelector') as HTMLInputElement).style.display === 'flex'
                                    ) {
                                        let docID = parseInt(modal.getAttribute('data-docID'));
                                        let ProcRefs = window.selectedControls ?? [];
                                        let controlIDs = [];

                                        ProcRefs.forEach((control) => {
                                            controlIDs.push(control.controlID);
                                        });

                                        let fileSourceSelectValue = (modal.querySelector('#fileSourceSelectEdit') as HTMLInputElement).value;

                                        if (fileSourceSelectValue === 'confluence') {
                                            let pageID = (modal
                                                .querySelector('#pageIDEdit') as HTMLInputElement)
                                                .value.split('pages/')[1]
                                                .split('/', 1)[0];
                                            console.log(pageID);
                                            if (doc instanceof Document) {
                                                doc.docID = docID.toString();
                                                doc.pageName = (modal.querySelector("#pageNameEdit") as HTMLInputElement).value;
                                                doc.pageID = pageID;
                                                doc.userID = (modal.querySelector("#ownerSelect") as HTMLInputElement).value;
                                                doc.lastReviewDate = new Date((modal.querySelector("#lastReviewDate") as HTMLInputElement).valueAsDate);
                                                doc.nextReviewDate = new Date((modal.querySelector("#nextReviewDate") as HTMLInputElement).valueAsDate);
                                                doc.classification = (modal.querySelector("#classificationSelect") as HTMLSelectElement).value;
                                                doc.comments = (modal.querySelector("#fileComment") as HTMLInputElement).value;
                                                doc.procRef = controlIDs;
                                                doc.confluenceURL = (modal.querySelector("#pageIDEdit") as HTMLInputElement).value;
                                                doc.edit = true;

                                                await doc.upload("confluence").then(data => {
                                                    console.log(data);
                                                    (e.target as HTMLElement).innerText = 'Save';
                                                    ModalImplementation.close();
                                                })
                                                    .catch((e) => {
                                                        console.error(e);
                                                    });
                                            }

                                        } else if (fileSourceSelectValue === 'file') {

                                            if (doc instanceof Document) {
                                                doc.docID = docID.toString();
                                                doc.originalFileName = doc.fileName;
                                                doc.fileName = (modal.querySelector("#fileInput") as HTMLInputElement).files.length > 0 ? (modal.querySelector("#fileInput") as HTMLInputElement).files[0].name : "";
                                                doc.userID = (modal.querySelector("#ownerSelect") as HTMLInputElement).value;
                                                doc.lastReviewDate = new Date((modal.querySelector("#lastReviewDate") as HTMLInputElement).valueAsDate);
                                                doc.nextReviewDate = new Date((modal.querySelector("#nextReviewDate") as HTMLInputElement).valueAsDate);
                                                doc.classification = (modal.querySelector("#classificationSelect") as HTMLSelectElement).value;
                                                doc.comments = (modal.querySelector("#fileComment") as HTMLInputElement).value;
                                                doc.procRef = controlIDs;
                                                doc.edit = true;

                                                console.log(doc)

                                                let file = (modal.querySelector("#fileInputEdit") as HTMLInputElement).files.length > 0 ? (modal.querySelector("#fileInputEdit") as HTMLInputElement).files[0] : null;

                                                await doc.upload("file", file).then(data => {
                                                    console.log(data);
                                                    (e.target as HTMLElement).innerText = 'Save';
                                                    ModalImplementation.close();
                                                })
                                                    .catch((e) => {
                                                        console.error(e);
                                                    });
                                            }
                                        }
                                    } else {
                                        if (doc instanceof Document) {
                                            let ProcRefs = window.selectedControls ?? [];
                                            let controlIDs = [];

                                            ProcRefs.forEach((control) => {
                                                controlIDs.push(control.controlID);
                                            });

                                            let fileSourceSelectValue =
                                                (modal.querySelector('#fileSourceSelect') as HTMLInputElement).value;

                                            if (fileSourceSelectValue === 'confluence') {
                                                let pageID = (modal
                                                    .querySelector('#pageID') as HTMLInputElement)
                                                    .value.split('pages/')[1]
                                                    .split('/', 1)[0];

                                                console.log(pageID);

                                                doc.pageName = (modal.querySelector("#pageName") as HTMLInputElement).value;
                                                doc.pageID = pageID;
                                                doc.userID = (modal.querySelector("#ownerSelect") as HTMLInputElement).value;
                                                doc.lastReviewDate = new Date((modal.querySelector("#lastReviewDate") as HTMLInputElement).valueAsDate);
                                                doc.nextReviewDate = new Date((modal.querySelector("#nextReviewDate") as HTMLInputElement).valueAsDate);
                                                doc.classification = (modal.querySelector("#classificationSelect") as HTMLSelectElement).value;
                                                doc.comments = (modal.querySelector("#fileComment") as HTMLInputElement).value;
                                                doc.procRef = controlIDs;
                                                doc.confluenceURL = (modal.querySelector("#pageID") as HTMLInputElement).value;

                                                await doc.upload("confluence").then(data => {
                                                    console.log(data);
                                                    (e.target as HTMLElement).innerText = 'Save';
                                                    ModalImplementation.close();
                                                })
                                                    .catch((e) => {
                                                        console.error(e);
                                                    });
                                            } else if (fileSourceSelectValue === 'file') {
                                                const dataToCheck = await db
                                                    .collection('customers')
                                                    .doc(window.companyID)
                                                    .collection('product')
                                                    .doc('policyPackages')
                                                    .collection('ISO27001keyDocuments')
                                                    .doc('otherDocs')
                                                    .get();

                                                let docCount = dataToCheck.data().doc_count;
                                                let newDocCount = docCount + 1;

                                                if ((modal.querySelector('#fileInputEdit') as HTMLInputElement).files.length <= 0) {
                                                    (modal.querySelector('#save_file_button') as HTMLElement).innerText = 'Save';
                                                    return ((modal.querySelector('.errorLabel') as HTMLElement).style.display = 'flex');
                                                }

                                                doc.docID = newDocCount.toString();
                                                doc.originalFileName = doc.fileName;
                                                doc.fileName = (modal.querySelector("#fileInput") as HTMLInputElement).files.length > 0 ? (modal.querySelector("#fileInput") as HTMLInputElement).files[0].name : "";
                                                doc.userID = (modal.querySelector("#ownerSelect") as HTMLInputElement).value;
                                                doc.lastReviewDate = new Date((modal.querySelector("#lastReviewDate") as HTMLInputElement).valueAsDate);
                                                doc.nextReviewDate = new Date((modal.querySelector("#nextReviewDate") as HTMLInputElement).valueAsDate);
                                                doc.classification = (modal.querySelector("#classificationSelect") as HTMLSelectElement).value;
                                                doc.comments = (modal.querySelector("#fileComment") as HTMLInputElement).value;
                                                doc.procRef = controlIDs;

                                                let file = (modal.querySelector("#fileInputEdit") as HTMLInputElement).files.length > 0 ? (modal.querySelector("#fileInputEdit") as HTMLInputElement).files[0] : null;

                                                await doc.upload("file", file, newDocCount).then(data => {
                                                    console.log(data);
                                                    (e.target as HTMLElement).innerText = 'Save';
                                                    ModalImplementation.close();
                                                })
                                                    .catch((e) => {
                                                        console.error(e);
                                                    });
                                            }
                                        }
                                    }
                                });

                            const close_btn1 = modal.querySelector('#close_btn1');
                            close_btn1.addEventListener('click', () => {
                                ModalImplementation.close();
                            });
                            const close_btn2 = modal.querySelector('#close_button_external');
                            close_btn2.addEventListener('click', () => {
                                ModalImplementation.close();
                            });
                            window.addEventListener('keydown', (e) => {
                                if (e.code === 'Escape' && modal.style.display === 'flex') {
                                    if (!!(modal.querySelector('#fileInput') as HTMLInputElement)?.files[0]) {
                                        const closeModal = document.getElementById('closeModal');
                                        closeModal.classList.remove('display_none');
                                        closeModal.innerHTML = `
                                    <div class="close-modal">\
                                    <div class="inner-close-modal">\
                                        <p>Are you sure you want to exit?</p>\
                                        <div class="close-modal-buttons-container">\
                                            <button id='save_modal_button' class='btn-primary btn my_btn_style'>\
                                            <span id='save_span'>Yes</span>\
                                            <div class="spinner-border text-light display_none" id='save_spinner' role="status">\
                                                <span class="sr-only">Loading...</span>\
                                            </div>\
                                            </button>\
                                            <button id='close_modal_button' class='btn-primary btn my_btn_style'>\
                                                <span id='save_span'>No</span>\
                                            </button>\
                                        </div>
                                        </div>\
                                </div>\
                                `;
                                        const closeButton = document.getElementById('close_modal_button');
                                        const save = document.getElementById('save_modal_button');
                                        closeButton.addEventListener('click', () => {
                                            closeModal.classList.add('display_none');
                                        });
                                        save.addEventListener('click', () => {
                                            ModalImplementation.close();
                                        });
                                    } else {
                                        ModalImplementation.close();
                                    }
                                }
                            });

                            // Handle Selected Controls button
                            let controlsSelected = modal.querySelector(`#controlsSelectedInputEdit`);
                            controlsSelected.addEventListener('click', async (event) => {
                                event.preventDefault();
                                if (modal.getAttribute('data-docID') !== null) {
                                    await db
                                        .collection('customers')
                                        .doc(window.companyID)
                                        .collection('product')
                                        .doc('policyPackages')
                                        .collection('ISO27001keyDocuments')
                                        .doc('otherDocs')
                                        .get()
                                        .then((otherDoc) => {
                                            if (otherDoc.exists) {
                                                if (doc instanceof Document) {
                                                    window.selectedControls = window.selectedControls !== null ? window.selectedControls : otherDoc.data().uploaded[doc.docID]?.procRef;
                                                    window.useFrameworksInsteadOfControls = false;
                                                    loadSelectorModal("#selectControlsModal", `#${modal.id}`, false, null, "controls")
                                                }
                                            }
                                        });
                                } else {
                                    window.selectedControls = [];
                                    window.useFrameworksInsteadOfControls = false;
                                    loadSelectorModal("#selectControlsModal", `#${modal.id}`, false, null, "controls")
                                }
                            });

                            // Handle History Button
                            // Select the history button within the modal
                            /* Original History Btn
                            const historyBtn = modal.querySelector('#historyBtn');

                            // Add click event listener to the history button
                            historyBtn.addEventListener('click', async (e) => { // Made the callback async to use 'await' inside
                                if (doc instanceof Document) {
                                    // Hide and remove the current modal
                                    modal.style.display = 'none';
                                    modal.remove();

                                    // Clone the history modal template
                                    let historyModal = document.getElementById('historyModal');
                                    let currentHistoryModal = historyModal.cloneNode(true) as HTMLElement;

                                    // Set the document ID as a data attribute for reference
                                    currentHistoryModal.setAttribute('data-docID', `${doc.docID}`);

                                    // Display the history modal
                                    currentHistoryModal.style.display = 'flex';
                                    document.body.appendChild(currentHistoryModal);

                                    // Add event listener to the close button of the history modal
                                    const close_btn1 = currentHistoryModal.querySelector('#close_btn1');
                                    close_btn1.addEventListener('click', () => {
                                        currentHistoryModal.style.display = 'none';
                                        currentHistoryModal.remove();
                                    });

                                    // Select the history accordion container where history entries will be inserted
                                    const historyAccordionDiv = currentHistoryModal.querySelector('.side-modal-inner-content #historyAccordionDiv');

                                    // Check if 'documentHistory' exists in the document
                                    if (doc?.documentHistory !== undefined) {
                                        // Extract history entries excluding 'historyCount'
                                        const historyKeys = Object.keys(doc.documentHistory)
                                            .filter(key => key !== 'historyCount')
                                            .sort((a, b) => parseInt(a) - parseInt(b)); // Assuming numerical keys

                                        // Iterate over each history entry
                                        for (const historyKey of historyKeys) {
                                            const historyEntry = doc.documentHistory[historyKey];

                                            // Check if 'changedDate' exists
                                            if (historyEntry?.changedDate !== undefined) {
                                                // Fetch the name of the user who made the change
                                                const changedByName = await nameFromUID(historyEntry.changedBy);

                                                // Format the changed date
                                                const formattedDate = historyEntry.changedDate.toDate().toLocaleDateString();

                                                // Initialize changeRows string
                                                let changeRows = '';

                                                // Iterate over each field change and build the HTML rows
                                                historyEntry.fieldChanges.forEach((change) => {
                                                    changeRows += `
                                <div class="d-flex flex-column w-100 align-items-between justify-content-between">
                                    <strong style="margin-bottom: -10px;">${change.fieldName.toTitleCase()}</strong>
                                    <br>
                                    <div class="d-flex flex-row gap-3 w-100 align-items-between justify-content-between">
                                        <p style="overflow-wrap: break-word; white-space: normal; overflow: hidden; text-overflow: ellipsis;">${change.oldValue !== undefined ? formatHistoryValue(change.oldValue) : ''}</p>
                                        <p><strong>➞</strong></p>
                                        <p style="overflow-wrap: break-word; white-space: normal; overflow: hidden; text-overflow: ellipsis;">
                                            ${change.newValue !== undefined ? formatHistoryValue(change.newValue).trim() === "" && change.fieldName === "fileName" ? formatHistoryValue(change.oldValue) : formatHistoryValue(change.newValue).trim() : ''}
                                        </p>
                                    </div>
                                </div>`;
                                                });

                                                // Build the history row with Changed By and Change Date
                                                let historyRow = `
                            <div class="accordion-item">
                                <h2 class="accordion-header" id="heading${historyKey}">
                                    <button class="accordion-button collapsed" type="button" data-coreui-toggle="collapse" data-coreui-target="#collapse${historyKey}" aria-expanded="false" aria-controls="collapse${historyKey}">
                                        Change Date - ${formattedDate}
                                    </button>
                                </h2>
                                <div id="collapse${historyKey}" class="accordion-collapse collapse" aria-labelledby="heading${historyKey}" data-bs-parent="#historyAccordionDiv">
                                    <div class="accordion-body">
                                        <div class="d-flex w-100 align-items-center justify-content-between mb-3">
                                            <p><strong>Changed By:</strong></p>
                                            <p>${changedByName ?? 'Unknown'}</p>
                                        </div>
                                        <div class="d-flex w-100 align-items-center justify-content-between mb-3">
                                            <p><strong>Change Date:</strong></p>
                                            <p>${formattedDate}</p>
                                        </div>
                                        <h6>Changes made:</h6>
                                        <div class="d-flex w-100 flex-column align-items-between justify-content-between riskChanges">${changeRows}</div>
                                    </div>
                                </div>
                            </div>
                        `;

                                                // Insert the history row into the history accordion container
                                                historyAccordionDiv.insertAdjacentHTML('beforeend', historyRow);
                                            }
                                        }
                                    } else {
                                        // If no history exists, display a message
                                        historyAccordionDiv.insertAdjacentHTML('beforeend', `
                    <div class="row mb-3 px-5">
                        <div class="col-12 d-flex align-items-start">
                            <em>No history available for this document.</em>
                        </div>
                    </div>
                `);
                                    }
                                }
                            });*/

                            const historyBtn = modal.querySelector('#historyBtn');

                            historyBtn.addEventListener('click', (e) => {
                                if (doc instanceof Document) {
                                    modal.style.display = 'none';
                                    modal.remove();
                                    let historyModal = document.getElementById('historyModal');
                                    let currentHistoryModal = (historyModal.cloneNode(true) as HTMLElement);
                                    currentHistoryModal.setAttribute('data-docID', `${doc.docID}`);
                                    currentHistoryModal.style.display = 'flex';
                                    document.body.appendChild(currentHistoryModal);
                                    const close_btn1 = currentHistoryModal.querySelector('#close_btn1');
                                    close_btn1.addEventListener('click', () => {
                                        currentHistoryModal.style.display = 'none';
                                        currentHistoryModal.remove();
                                    });
                                    if (doc.documentHistory !== undefined) {
                                        let sortedKeys = Object.keys(
                                            doc.documentHistory,
                                        ).sort((a: any, b: any) => a - b);

                                        for (const history of sortedKeys) {
                                            if (
                                                Object.keys(doc.documentHistory?.[history]).length > 1
                                            ) {
                                                let accordionDiv = document.createElement('div');
                                                let accordionHeader = document.createElement('div');
                                                let accordionLabel = document.createElement('label');
                                                let accordionIcon = document.createElement('i');
                                                let accordionBody = document.createElement('div');

                                                accordionDiv.classList.add('accordionStyleRowType');
                                                accordionHeader.classList.add('accordionHeader');
                                                accordionBody.classList.add('accordionBody');
                                                accordionIcon.classList.add('fa-sharp');
                                                accordionIcon.classList.add('fa-solid');
                                                accordionIcon.classList.add('fa-chevron-down');

                                                console.log(
                                                    doc.documentHistory?.[history]?.changedDate,
                                                );
                                                const changedDate = doc.documentHistory?.[history]?.changedDate;
                                                const formattedDate = changedDate instanceof FirebaseTimestamp
                                                    ? changedDate.toDate().toLocaleDateString()
                                                    : changedDate instanceof Date
                                                        ? changedDate.toLocaleDateString()
                                                        : new Date().toLocaleDateString();
                                                accordionLabel.innerText = 'Change made ' + formattedDate;
                                                if (doc.documentHistory?.[history]?.fileName !== undefined) {
                                                    accordionBody.innerHTML +=
                                                        '<p><b>Old file name:</b>&nbsp;' +
                                                        (doc.documentHistory?.[history]?.oldFileName ?? "N/A") + "</p>";
                                                    accordionBody.innerHTML +=
                                                        '<p><b>New file name:</b>&nbsp;' +
                                                        doc.documentHistory?.[history].fileName + "</p>";
                                                }
                                                if (doc.documentHistory?.[history]?.comments !== undefined) {
                                                    accordionBody.innerHTML +=
                                                        '<p><b>Old comments:</b>&nbsp;' +
                                                        (doc.documentHistory?.[history]?.oldComments ?? 'N/A') + "</p>";
                                                    accordionBody.innerHTML +=
                                                        '<p><b>New comments:</b>&nbsp;' +
                                                        doc.documentHistory?.[history].comments + "</p>";
                                                }
                                                if (doc.documentHistory?.[history]?.procRef !== undefined) {
                                                    accordionBody.innerHTML +=
                                                        '<p><b>Old control IDs:</b>&nbsp;' +
                                                        (doc.documentHistory?.[history]?.oldProcRef?.join(", ") ?? "N/A") + "</p>";
                                                    accordionBody.innerHTML +=
                                                        '<p><b>Control IDs:</b>&nbsp;' +
                                                        doc.documentHistory?.[history].procRef.join(", ") + "</p>";
                                                }
                                                if (doc.documentHistory?.[history]?.uploadedBy !== undefined) {
                                                    db.collection("users").doc(auth.currentUser.uid).get().then(async (user) => {
                                                        if (user.exists && doc instanceof Document) {
                                                            let roles = user.data().roles;
                                                            if (roles?.msp === true) {
                                                                for (const assignee of window.assignees) {
                                                                    if (assignee?.userID === doc.documentHistory?.[history]?.uploadedBy) {
                                                                        accordionBody.innerHTML +=
                                                                            '<p><b>Last updated by:</b>&nbsp;' +
                                                                            `${assignee.firstName} ${assignee.lastName}` + "</p>";
                                                                    } else if (assignee?.mspUser === true && assignee?.mspUsers?.includes(doc.documentHistory?.[history]?.uploadedBy)) {
                                                                        accordionBody.innerHTML +=
                                                                            '<p><b>Last updated by:</b>&nbsp;' +
                                                                            assignee.companyName || assignee.firstName + ' ' + assignee.lastName + "</p>";
                                                                    }
                                                                    if (doc.documentHistory?.[history]?.oldUploadedBy !== undefined) {
                                                                        if (assignee?.userID === doc.documentHistory?.[history]?.oldUploadedBy) {
                                                                            accordionBody.innerHTML +=
                                                                                '<p><b>Old last updated by:</b>&nbsp;' +
                                                                                `${assignee.firstName} ${assignee.lastName}` + "</p>";
                                                                        } else if (assignee?.mspUser === true && assignee?.mspUsers?.includes(doc.documentHistory?.[history]?.oldUploadedBy)) {
                                                                            accordionBody.innerHTML +=
                                                                                '<p><b>Old last updated by:</b>&nbsp;' +
                                                                                assignee.companyName || assignee.firstName + ' ' + assignee.lastName + "</p>";
                                                                        }
                                                                    } else {
                                                                        accordionBody.innerHTML +=
                                                                            '<p><b>Old last updated by:</b>&nbsp;' +
                                                                            "N/A" + "</p>";
                                                                        break;
                                                                    }
                                                                }
                                                            } else {
                                                                for (const assignee of window.assignees) {
                                                                    if (assignee?.mspUser === true && assignee?.mspUsers?.includes(doc.documentHistory?.[history]?.uploadedBy)) {
                                                                        accordionBody.innerHTML +=
                                                                            '<p><b>Last updated by:</b>&nbsp;' +
                                                                            assignee.companyName || assignee.firstName + ' ' + assignee.lastName + "</p>";
                                                                    } else if (assignee?.userID === doc.documentHistory?.[history]?.uploadedBy) {
                                                                        accordionBody.innerHTML +=
                                                                            '<p><b>Last updated by:</b>&nbsp;' +
                                                                            `${assignee.firstName} ${assignee.lastName}` + "</p>";
                                                                    }
                                                                    if (doc.documentHistory?.[history]?.oldUploadedBy !== undefined) {
                                                                        if (assignee?.mspUser === true && assignee?.mspUsers?.includes(doc.documentHistory?.[history]?.oldUploadedBy)) {
                                                                            accordionBody.innerHTML +=
                                                                                '<p><b>Old last updated by:</b>&nbsp;' +
                                                                                assignee.companyName || assignee.firstName + ' ' + assignee.lastName + "</p>";
                                                                        } else if (assignee?.userID === doc.documentHistory?.[history]?.oldUploadedBy) {
                                                                            accordionBody.innerHTML +=
                                                                                '<p><b>Old last updated by:</b>&nbsp;' +
                                                                                `${assignee.firstName} ${assignee.lastName}` + "</p>";
                                                                        }
                                                                    } else {
                                                                        accordionBody.innerHTML +=
                                                                            '<p><b>Old last updated by:</b>&nbsp;' +
                                                                            `N/A` + "</p>";
                                                                        break;
                                                                    }
                                                                }
                                                            }
                                                        }
                                                    })
                                                }
                                                if (doc.documentHistory?.[history]?.lastReviewDate !== undefined) {
                                                    accordionBody.innerHTML +=
                                                        '<p><b>Old last review date:</b>&nbsp;' +
                                                        (doc.documentHistory?.[history]?.oldLastReviewDate instanceof FirebaseTimestamp
                                                            ? doc.documentHistory?.[history]?.oldLastReviewDate.toDate().toLocaleDateString()
                                                            : doc.documentHistory?.[history]?.oldLastReviewDate instanceof Date
                                                                ? doc.documentHistory?.[history]?.oldLastReviewDate.toLocaleDateString()
                                                                : "N/A") + "</p>";
                                                    accordionBody.innerHTML +=
                                                        '<p><b>Last review date:</b>&nbsp;' +
                                                        (doc.documentHistory?.[history]?.lastReviewDate instanceof FirebaseTimestamp
                                                            ? doc.documentHistory?.[history]?.lastReviewDate.toDate().toLocaleDateString()
                                                            : doc.documentHistory?.[history]?.lastReviewDate instanceof Date
                                                                ? doc.documentHistory?.[history]?.lastReviewDate.toLocaleDateString()
                                                                : new Date().toLocaleDateString()) + "</p>";
                                                }
                                                if (doc.documentHistory?.[history]?.nextReviewDate !== undefined) {
                                                    accordionBody.innerHTML +=
                                                        '<p><b>Old next review date:</b>&nbsp;' +
                                                        (doc.documentHistory?.[history]?.oldNextReviewDate instanceof FirebaseTimestamp
                                                            ? doc.documentHistory?.[history]?.oldNextReviewDate.toDate().toLocaleDateString()
                                                            : doc.documentHistory?.[history]?.oldNextReviewDate instanceof Date
                                                                ? doc.documentHistory?.[history]?.oldNextReviewDate.toLocaleDateString()
                                                                : "N/A") + "</p>";
                                                    accordionBody.innerHTML +=
                                                        '<p><b>Next review date:</b>&nbsp;' +
                                                        (doc.documentHistory?.[history]?.nextReviewDate instanceof FirebaseTimestamp
                                                            ? doc.documentHistory?.[history]?.nextReviewDate.toDate().toLocaleDateString()
                                                            : doc.documentHistory?.[history]?.nextReviewDate instanceof Date
                                                                ? doc.documentHistory?.[history]?.nextReviewDate.toLocaleDateString()
                                                                : new Date().toLocaleDateString()) + "</p>";
                                                }
                                                if (doc.documentHistory?.[history]?.fileName !== undefined) {
                                                    accordionBody.innerHTML += `<button id="restoreFile_${history}" data-docID="${doc.docID}">Restore previous file</button>`
                                                }

                                                accordionHeader.appendChild(accordionLabel);
                                                accordionHeader.appendChild(accordionIcon);
                                                accordionDiv.appendChild(accordionHeader);
                                                accordionDiv.appendChild(accordionBody);
                                                currentHistoryModal
                                                    .querySelector('.side-modal-inner-content')
                                                    .appendChild(accordionDiv);

                                                accordionHeader.addEventListener('click', (e) => {
                                                    if (accordionBody.style.display === 'flex') {
                                                        accordionBody.style.display = 'none';
                                                        accordionIcon.classList.remove('fa-chevron-up');
                                                        accordionIcon.classList.add('fa-chevron-down');
                                                    } else {
                                                        accordionBody.style.display = 'flex';
                                                        accordionIcon.classList.remove('fa-chevron-down');
                                                        accordionIcon.classList.add('fa-chevron-up');
                                                    }
                                                });

                                                if (doc.documentHistory?.[history]?.fileName !== undefined) {
                                                    document.querySelector(`#restoreFile_${history}`).addEventListener('click', async (e) => {
                                                        (e.target as HTMLElement).innerHTML = `<i class="fa fa-spinner fa-spin"></i>`;
                                                        const docID = (e.target as HTMLElement).getAttribute('data-docID');
                                                        const historyID = (e.target as HTMLElement).id.replace('restoreFile_', '');
                                                        const supportingDocsDoc = await db.collection("customers").doc(window.companyID).collection("product").doc("policyPackages").collection("ISO27001keyDocuments").doc("otherDocs").get();
                                                        const supportingDocs = supportingDocsDoc.data().uploaded;
                                                        const supportingDoc = supportingDocs[docID];
                                                        const currentHistoryRef = supportingDoc.documentHistory[historyID];
                                                        console.log(currentHistoryRef)
                                                        const fileName = currentHistoryRef.oldFileName;
                                                        const apiResponse = await deiterateAPI.POST("/deiterateInternalAPI/saveSupportingDoc", {
                                                            mode: "cors",
                                                            body: JSON.stringify({
                                                                callType: 'file',
                                                                manualFileData: {
                                                                    fileName: fileName,
                                                                    customerID: window.companyID,
                                                                    userID: supportingDoc.uploadedBy,
                                                                    lastReviewDate: supportingDoc.lastReviewDate instanceof FirebaseTimestamp
                                                                        ? supportingDoc.lastReviewDate.toDate()
                                                                        : supportingDoc.lastReviewDate instanceof Date
                                                                            ? supportingDoc.lastReviewDate
                                                                            : new Date(),
                                                                    nextReviewDate: supportingDoc.nextReviewDate instanceof FirebaseTimestamp
                                                                        ? supportingDoc.nextReviewDate.toDate()
                                                                        : supportingDoc.nextReviewDate instanceof Date
                                                                            ? supportingDoc.nextReviewDate
                                                                            : new Date(),
                                                                    docClassification: supportingDoc.classification,
                                                                    docComments: supportingDoc.comments,
                                                                    docProcRefs: supportingDoc.procRef,
                                                                    edit: true,
                                                                    docID: docID,
                                                                    docHash: currentHistoryRef.oldDocHash,
                                                                    msgPages: supportingDoc.msgPages,
                                                                },
                                                            })
                                                        })
                                                        if (apiResponse.success === true) {
                                                            currentHistoryModal.style.display = 'none';
                                                            currentHistoryModal.remove();
                                                        } else {
                                                            Swal.fire({
                                                                title: 'Error',
                                                                text: 'An error occurred while restoring the file. Please try again later.',
                                                                icon: 'error',
                                                                confirmButtonText: 'Okay',
                                                            });
                                                        }
                                                    })
                                                }
                                            }
                                        }
                                    }
                                }
                            });



                            // Handle User Close
                            modal.addEventListener('click', function (event: Event) {
                                // Check if the clicked element is the parent, not one of its children
                                console.log('CLicked');
                                if (event.target === this) {
                                    ModalImplementation.close();
                                }
                            });

                            window.addEventListener('keydown', function (event: KeyboardEvent) {
                                if (event.key === 'Escape') {
                                    ModalImplementation.close();
                                }
                            });
                        }
                    } else {
                        modal.id = `newSupportingDoc`;
                        this.currentModalID = modal.id;
                        window.supportingDocsModalID = modal.id;
                        modal.style.display = 'flex';

                        (modal.querySelector("#historyBtn") as HTMLElement).style.display = 'none';

                        (modal.querySelector('#fileSelector') as HTMLElement).style.display = 'flex';
                        (modal.querySelector('#editFileSelector') as HTMLElement).style.display = 'none';
                        (modal.querySelector('#deleteBtn') as HTMLElement).style.display = 'flex';
                        (modal.querySelector('#controlsSelectedInputEdit') as HTMLInputElement).value = '';
                        (modal.querySelector('#deleteBtn') as HTMLInputElement).style.display = 'none';
                        (modal.querySelector('#editFileSelector') as HTMLInputElement).style.display = 'none';
                        (modal.querySelector('#sideModalHeader') as HTMLElement).innerText = 'Upload files';
                        (modal.querySelector('#lastReviewDate') as HTMLInputElement).valueAsDate = new Date();
                        (modal.querySelector('#nextReviewDate') as HTMLInputElement).valueAsDate = new Date().addDays(365);

                        const rootCustomerDoc = await db.collection('customers').doc(window.companyID).get();
                        if (rootCustomerDoc.data()?.customerPlan !== undefined) {
                            (modal.querySelector('#dataClassificationDiv') as HTMLElement).style.display = 'none';
                        }

                        let controlsSelected = (modal.querySelector(`#controlsSelectedInputEdit`) as HTMLInputElement);
                        controlsSelected.addEventListener('click', async (event) => {
                            event.preventDefault();
                            if (modal.getAttribute('data-docID') !== null) {
                                await db
                                    .collection('customers')
                                    .doc(window.companyID)
                                    .collection('product')
                                    .doc('policyPackages')
                                    .collection('ISO27001keyDocuments')
                                    .doc('otherDocs')
                                    .get()
                                    .then((otherDoc) => {
                                        if (otherDoc.exists) {
                                            let docID = parseInt(modal?.getAttribute('data-docID') ?? "0");

                                            window.selectedControls =
                                                window.selectedControls !== null
                                                    ? window.selectedControls
                                                    : otherDoc.data().uploaded[docID]?.procRef;
                                            window.useFrameworksInsteadOfControls = false;
                                            loadSelectorModal("#selectControlsModal", `#${modal.id}`, false, null, "controls")
                                        }
                                    });
                            } else {
                                window.selectedControls = [];
                                window.useFrameworksInsteadOfControls = false;
                                loadSelectorModal("#selectControlsModal", `#${modal.id}`, false, null, "controls")
                                loadSelectorModal("#selectControlsModal", `#${modal.id}`, false, null, "controls")
                            }
                        });

                        const ownerSelect = modal.querySelector('#ownerSelect');
                        ownerSelect.innerHTML = '';
                        modal.querySelector('#classificationSelect').innerHTML = '';
                        const complianceOptions = await db
                            .collection('customers')
                            .doc(window.companyID)
                            .collection('compliance')
                            .doc('asset')
                            .get();

                        if (complianceOptions.data()?.register?.menu?.classification !== undefined) {
                            complianceOptions.data()?.register?.menu?.classification?.forEach((optionValue: string) => {
                                const option = document.createElement('option');
                                option.innerHTML = optionValue;
                                option.value = optionValue;
                                modal.querySelector('#classificationSelect')?.appendChild(option);
                            });
                        } else {
                            let classification = ['Customer Confidential', 'Business Confidential', 'Public'];
                            classification.forEach((optionValue) => {
                                const option = document.createElement('option');
                                option.innerHTML = optionValue;
                                option.value = optionValue;
                                modal.querySelector('#classificationSelect')?.appendChild(option);
                            });
                        }

                        await db
                            .collection('customers')
                            .doc(window.companyID)
                            .get()
                            .then(async (companyDoc) => {
                                if (companyDoc.exists) {
                                    const customerUsers = Object.keys(companyDoc.data().customerOrg.users).sort();
                                    for (const user of customerUsers) {
                                        let usersUID = companyDoc.data().customerOrg.users[user].userID;
                                        await nameFromUID(usersUID)
                                            .then((name) => {
                                                let ownerOption = document.createElement('option');
                                                ownerOption.id = usersUID;
                                                ownerOption.value = usersUID;
                                                ownerOption.innerText = name;
                                                ownerSelect.appendChild(ownerOption);
                                            })
                                            .catch((e) => {
                                                console.error(e);
                                            });
                                    }
                                }
                            });

                        modal.querySelector('#fileSourceSelect').addEventListener('change', (e) => {
                            if ((e.target as HTMLInputElement).value === 'file') {
                                (modal.querySelector('#manualFileUploadDiv') as HTMLElement).style.display = 'flex';
                                (modal.querySelector('#confluenceFileUpload') as HTMLElement).style.display = 'none';
                            } else if ((e.target as HTMLInputElement).value === 'confluence') {
                                (modal.querySelector('#confluenceFileUpload') as HTMLElement).style.display = 'flex';
                                (modal.querySelector('#manualFileUploadDiv') as HTMLElement).style.display = 'none';
                            }
                        });

                        modal.querySelector('#fileSourceSelectEdit').addEventListener('change', (e) => {
                            if ((e.target as HTMLInputElement).value === 'file') {
                                (modal.querySelector('#manualFileUploadDivEdit') as HTMLElement).style.display = 'flex';
                                (modal.querySelector('#confluenceFileUploadEdit') as HTMLElement).style.display = 'none';
                            } else if ((e.target as HTMLInputElement).value === 'confluence') {
                                (modal.querySelector('#confluenceFileUploadEdit') as HTMLElement).style.display = 'flex';
                                (modal.querySelector('#manualFileUploadDivEdit') as HTMLElement).style.display = 'none';
                            }
                        });

                        modal.querySelector('#pageID').addEventListener('change', (e) => {
                            if (!(e.target as HTMLInputElement).value.includes('pages')) {
                                modal.querySelector('.pageUrlDiv').classList.add('incorrectFormat');
                                (modal.querySelector('#warningIcon') as HTMLElement).style.display = 'flex';
                                (modal.querySelector('#save_file_button') as HTMLInputElement).disabled = true;
                            } else {
                                modal.querySelector('.pageUrlDiv').classList.remove('incorrectFormat');
                                (modal.querySelector('#warningIcon') as HTMLElement).style.display = 'none';
                                (modal.querySelector('#save_file_button') as HTMLInputElement).disabled = false;
                            }
                        });

                        modal.querySelector('#fileUploadSelector').addEventListener('click', (e) => {
                            (modal.querySelector('#fileInput') as HTMLInputElement).click();
                        });

                        modal.querySelector('#fileUploadSelectorEdit').addEventListener('click', (e) => {
                            (modal.querySelector('#fileInputEdit') as HTMLInputElement).click();
                        });

                        modal.querySelector('#fileInput').addEventListener('change', (e) => {
                            if ((e.target as HTMLInputElement).files.length > 0) {
                                (modal.querySelector('#uploadText') as HTMLInputElement).innerText = sanitise((e.target as HTMLInputElement).files[0].name);
                                (modal.querySelector('#removeIcon') as HTMLInputElement).style.display = 'flex';
                                (modal.querySelector('#uploadIcon') as HTMLInputElement).style.display = 'none';
                            } else {
                                (modal.querySelector('#uploadText') as HTMLInputElement).innerText = 'Click to upload a file';
                                (modal.querySelector('#removeIcon') as HTMLInputElement).style.display = 'none';
                                (modal.querySelector('#uploadIcon') as HTMLInputElement).style.display = 'flex';
                            }
                        });

                        modal.querySelector('#fileInputEdit').addEventListener('change', (e) => {
                            if ((e.target as HTMLInputElement).files.length > 0) {
                                (modal.querySelector('#uploadTextEdit') as HTMLInputElement).innerText = sanitise((e.target as HTMLInputElement).files[0].name);
                                (modal.querySelector('#removeIconEdit') as HTMLInputElement).style.display = 'flex';
                                (modal.querySelector('#uploadIconEdit') as HTMLInputElement).style.display = 'none';
                            } else {
                                (modal.querySelector('#uploadTextEdit') as HTMLInputElement).innerText = 'Click to upload a file';
                                (modal.querySelector('#removeIconEdit') as HTMLInputElement).style.display = 'none';
                                (modal.querySelector('#uploadIconEdit') as HTMLInputElement).style.display = 'flex';
                            }
                        });

                        (modal.querySelector('#removeIcon') as HTMLInputElement).addEventListener('click', () => {
                            (modal.querySelector('#fileInput') as HTMLInputElement).files = null;
                        });

                        modal.querySelector('#deleteBtn').addEventListener('click', async (e) => {
                            await db
                                .doc(`/customers/${window.companyID}/product/policyPackages/ISO27001keyDocuments/otherDocs`)
                                .get()
                                .then(async (otherDocs) => {
                                    if (otherDocs.exists) {
                                        let docID = parseInt(modal.getAttribute('data-docID'));
                                        (e.target as HTMLElement).innerHTML = `<i class="fa fa-spinner fa-spin"></i>`;
                                        const storageDocs = storageRef.child(
                                            `/customers/${window.companyID}/keyDocuments/otherDocs/uploaded/${docID}/${otherDocs.data().uploaded[docID].fileName}`,
                                        );
                                        try {
                                            await storageDocs.delete();
                                        } catch (error) {
                                            console.error('DELETE FILE ERROR', error);
                                        }
                                        await db
                                            .collection('customers')
                                            .doc(window.companyID)
                                            .collection('product')
                                            .doc('policyPackages')
                                            .collection('ISO27001keyDocuments')
                                            .doc('otherDocs')
                                            .set(
                                                {
                                                    uploaded: {
                                                        [docID]: FirebaseFieldValue.delete(),
                                                    },
                                                },
                                                { merge: true },
                                            )
                                            .then(() => {
                                                (e.target as HTMLElement).innerText = `Delete`;
                                                modal.style.display = 'none';
                                                modal.remove();
                                            })
                                            .catch((error) => {
                                                console.log(error);
                                            });
                                    }
                                });
                        });

                        modal.querySelector('#save_file_button').addEventListener('click', async (e) => {
                            (e.target as HTMLElement).innerHTML = `<i class="fa fa-spinner fa-spin"></i>`;

                            if ((modal.querySelector('#editFileSelector') as HTMLInputElement).style.display === 'flex') {
                                let docID = parseInt(modal.getAttribute('data-docID'));
                                let ProcRefs = window.selectedControls ?? [];
                                let controlIDs = [];

                                ProcRefs.forEach((control) => {
                                    controlIDs.push(control.controlID);
                                });

                                let fileSourceSelectValue = (modal.querySelector('#fileSourceSelectEdit') as HTMLInputElement).value;

                                if (fileSourceSelectValue === 'confluence') {
                                    let pageID = (modal
                                        .querySelector('#pageIDEdit') as HTMLInputElement)
                                        .value.split('pages/')[1]
                                        .split('/', 1)[0];
                                    console.log(pageID);
                                    doc = new Document();
                                    doc.docID = docID.toString();
                                    doc.pageName = (modal.querySelector("#pageNameEdit") as HTMLInputElement).value;
                                    doc.pageID = pageID;
                                    doc.userID = (modal.querySelector("#ownerSelect") as HTMLInputElement).value;
                                    doc.lastReviewDate = new Date((modal.querySelector("#lastReviewDate") as HTMLInputElement).valueAsDate);
                                    doc.nextReviewDate = new Date((modal.querySelector("#nextReviewDate") as HTMLInputElement).valueAsDate);
                                    doc.classification = (modal.querySelector("#classificationSelect") as HTMLSelectElement).value;
                                    doc.comments = (modal.querySelector("#fileComment") as HTMLInputElement).value;
                                    doc.procRef = controlIDs;
                                    doc.confluenceURL = (modal.querySelector("#pageIDEdit") as HTMLInputElement).value;
                                    doc.edit = true;

                                    await doc.upload("confluence").then(data => {
                                        console.log(data);
                                        (e.target as HTMLElement).innerText = 'Save';
                                        ModalImplementation.close();
                                    })
                                        .catch((e) => {
                                            console.error(e);
                                        });
                                    // auth.currentUser.getIdToken(true).then(async function (idToken) {
                                    //     let fetchURL = (window.location.hostname.includes('localhost') || window.location.hostname.includes("127.0.0.1"))
                                    //         ? window.apiURL
                                    //         : import.meta.env.VITE_cloudFunctionURL;
                                    //     await fetch(
                                    //         fetchURL + '/deiterateInternalAPI/saveSupportingDoc',
                                    //         {
                                    //             method: 'POST',
                                    //             headers: {
                                    //                 'Content-Type': 'application/json',
                                    //                 Authorization: `Bearer ${idToken}`,
                                    //                 'Company-ID': window.companyID,
                                    //             },
                                    //             mode: 'cors',
                                    //             body: JSON.stringify({
                                    //                 callType: 'confluence',
                                    //                 confluenceData: {
                                    //                     pageName: modal.querySelector('#pageNameEdit').value,
                                    //                     pageID: pageID,
                                    //                     customerID: window.companyID,
                                    //                     userID: modal.querySelector('#ownerSelect').value,
                                    //                     lastReviewDate: new Date(
                                    //                         modal.querySelector('#lastReviewDate').valueAsDate,
                                    //                     ),
                                    //                     nextReviewDate: new Date(
                                    //                         modal.querySelector('#nextReviewDate').valueAsDate,
                                    //                     ),
                                    //                     docClassification: modal.querySelector('#classificationSelect').value,
                                    //                     docComments: modal.querySelector('#fileComment').value,
                                    //                     docProcRefs: controlIDs,
                                    //                     confluenceURL: modal.querySelector('#pageIDEdit').value,
                                    //                     edit: true,
                                    //                     docID: docID,
                                    //                 },
                                    //             }),
                                    //         },
                                    //     )
                                    //         .then((res) => res.json()).then((data) => {
                                    //             console.log(data);
                                    //             e.target.innerText = 'Save';
                                    //             modal.style.display = 'none';
                                    //             modal.remove();
                                    //             let docsTable = document.getElementById('docsTable');
                                    //             docsTable?.remove();
                                    //             db.collection('customers')
                                    //                 .doc(companyID)
                                    //                 .get()
                                    //                 .then((customerDocument) => {
                                    //                     if (customerDocument.exists) {
                                    //                         firstTimeExecution();
                                    //                     }
                                    //                 });
                                    //         })
                                    //         .catch((e) => {
                                    //             console.error(e);
                                    //         });
                                    // });
                                } else if (fileSourceSelectValue === 'file') {
                                    doc = new Document();
                                    doc.docID = docID.toString();
                                    doc.originalFileName = doc.fileName;
                                    doc.fileName = (modal.querySelector("#fileInput") as HTMLInputElement).files.length > 0 ? (modal.querySelector("#fileInput") as HTMLInputElement).files[0].name : "";
                                    doc.userID = (modal.querySelector("#ownerSelect") as HTMLInputElement).value;
                                    doc.lastReviewDate = new Date((modal.querySelector("#lastReviewDate") as HTMLInputElement).valueAsDate);
                                    doc.nextReviewDate = new Date((modal.querySelector("#nextReviewDate") as HTMLInputElement).valueAsDate);
                                    doc.classification = (modal.querySelector("#classificationSelect") as HTMLSelectElement).value;
                                    doc.comments = (modal.querySelector("#fileComment") as HTMLInputElement).value;
                                    doc.procRef = controlIDs;
                                    doc.edit = true;

                                    console.log(doc)

                                    let file = (modal.querySelector("#fileInputEdit") as HTMLInputElement).files.length > 0 ? (modal.querySelector("#fileInputEdit") as HTMLInputElement).files[0] : null;

                                    await doc.upload("file", file).then(data => {
                                        console.log(data);
                                        (e.target as HTMLElement).innerText = 'Save';
                                        ModalImplementation.close();
                                    })
                                        .catch((e) => {
                                            console.error(e);
                                        });
                                }
                            } else {
                                let ProcRefs = window.selectedControls ?? [];
                                let controlIDs = [];

                                ProcRefs.forEach((control) => {
                                    controlIDs.push(control.controlID);
                                });

                                let fileSourceSelectValue =
                                    (modal.querySelector('#fileSourceSelect') as HTMLInputElement).value;

                                if (fileSourceSelectValue === 'confluence') {
                                    let pageID = (modal
                                        .querySelector('#pageID') as HTMLInputElement)
                                        .value.split('pages/')[1]
                                        .split('/', 1)[0];

                                    console.log(pageID);

                                    doc = new Document();
                                    doc.pageName = (modal.querySelector("#pageName") as HTMLInputElement).value;
                                    doc.pageID = pageID;
                                    doc.userID = (modal.querySelector("#ownerSelect") as HTMLInputElement).value;
                                    doc.lastReviewDate = new Date((modal.querySelector("#lastReviewDate") as HTMLInputElement).valueAsDate);
                                    doc.nextReviewDate = new Date((modal.querySelector("#nextReviewDate") as HTMLInputElement).valueAsDate);
                                    doc.classification = (modal.querySelector("#classificationSelect") as HTMLSelectElement).value;
                                    doc.comments = (modal.querySelector("#fileComment") as HTMLInputElement).value;
                                    doc.procRef = controlIDs;
                                    doc.confluenceURL = (modal.querySelector("#pageID") as HTMLInputElement).value;

                                    await doc.upload("confluence").then(data => {
                                        console.log(data);
                                        (e.target as HTMLElement).innerText = 'Save';
                                        ModalImplementation.close();
                                    })
                                        .catch((e) => {
                                            console.error(e);
                                        });
                                } else if (fileSourceSelectValue === 'file') {
                                    const dataToCheck = await db
                                        .collection('customers')
                                        .doc(window.companyID)
                                        .collection('product')
                                        .doc('policyPackages')
                                        .collection('ISO27001keyDocuments')
                                        .doc('otherDocs')
                                        .get();

                                    let docCount = dataToCheck.data().doc_count;
                                    let newDocCount = docCount + 1;

                                    if ((modal.querySelector('#fileInput') as HTMLInputElement).files.length <= 0) {
                                        (modal.querySelector('#save_file_button') as HTMLElement).innerText = 'Save';
                                        return ((modal.querySelector('.errorLabel') as HTMLElement).style.display = 'flex');
                                    }

                                    doc = new Document(
                                        {
                                            companyID: window.companyID,
                                            docID: newDocCount.toString(),
                                            fileName: (modal.querySelector("#fileInput") as HTMLInputElement).files.length > 0 ? (modal.querySelector("#fileInput") as HTMLInputElement).files[0].name : "",
                                            userID: (modal.querySelector("#ownerSelect") as HTMLInputElement).value,
                                            lastReviewDate: new Date((modal.querySelector("#lastReviewDate") as HTMLInputElement).valueAsDate),
                                            nextReviewDate: new Date((modal.querySelector("#nextReviewDate") as HTMLInputElement).valueAsDate),
                                            classification: (modal.querySelector("#classificationSelect") as HTMLSelectElement).value,
                                            comments: (modal.querySelector("#fileComment") as HTMLInputElement).value,
                                            procRef: controlIDs,
                                        }
                                    );

                                    // doc.docID = newDocCount.toString();
                                    // doc.fileName = (modal.querySelector("#fileInput") as HTMLInputElement).files.length > 0 ? (modal.querySelector("#fileInput") as HTMLInputElement).files[0].name : "";
                                    // doc.userID = (modal.querySelector("#ownerSelect") as HTMLInputElement).value;
                                    // doc.lastReviewDate = new Date((modal.querySelector("#lastReviewDate") as HTMLInputElement).valueAsDate);
                                    // doc.nextReviewDate = new Date((modal.querySelector("#nextReviewDate") as HTMLInputElement).valueAsDate);
                                    // doc.classification = (modal.querySelector("#classificationSelect") as HTMLSelectElement).value;
                                    // doc.comments = (modal.querySelector("#fileComment") as HTMLInputElement).value;
                                    // doc.procRef = controlIDs;

                                    let file = (modal.querySelector("#fileInput") as HTMLInputElement).files.length > 0 ? (modal.querySelector("#fileInput") as HTMLInputElement).files[0] : null;

                                    await doc.upload("file", file, newDocCount).then(data => {
                                        console.log(data);
                                        (e.target as HTMLElement).innerText = 'Save';
                                        ModalImplementation.close();
                                    })
                                        .catch((e) => {
                                            console.error(e);
                                        });
                                }
                            }
                        });

                        const close_btn1 = modal.querySelector('#close_btn1');
                        close_btn1.addEventListener('click', () => {
                            modal.style.display = 'none';
                            modal.remove();
                        });
                        const close_btn2 = modal.querySelector('#close_button_external');
                        close_btn2.addEventListener('click', () => {
                            modal.style.display = 'none';
                            modal.remove();
                        });
                        window.addEventListener('keydown', (e) => {
                            if (e.code === 'Escape' && modal.style.display === 'flex') {
                                if (!!(modal.querySelector('#fileInput') as HTMLInputElement)?.files[0]) {
                                    const closeModal = document.getElementById('closeModal');
                                    closeModal.classList.remove('display_none');
                                    closeModal.innerHTML = `
          <div class="close-modal">\
            <div class="inner-close-modal">\
                <p>Are you sure you want to exit?</p>\
                <div class="close-modal-buttons-container">\
                    <button id='save_modal_button' class='btn-primary btn my_btn_style'>\
                    <span id='save_span'>Yes</span>\
                    <div class="spinner-border text-light display_none" id='save_spinner' role="status">\
                        <span class="sr-only">Loading...</span>\
                    </div>\
                    </button>\
                    <button id='close_modal_button' class='btn-primary btn my_btn_style'>\
                        <span id='save_span'>No</span>\
                    </button>\
                </div>
                </div>\
        </div>\
        `;
                                    const closeButton = document.getElementById('close_modal_button');
                                    const save = document.getElementById('save_modal_button');
                                    closeButton.addEventListener('click', () => {
                                        closeModal.classList.add('display_none');
                                    });
                                    save.addEventListener('click', () => {
                                        // modal.style.display = 'none';
                                        ModalImplementation.close();
                                        closeModal.classList.add('display_none');
                                    });
                                } else {
                                    // modal.style.display = 'none';
                                    // modal.remove();
                                    ModalImplementation.close();
                                }
                            }
                        });
                        modal.addEventListener('click', function (event: Event) {
                            // Check if the clicked element is the parent, not one of its children
                            console.log('CLicked');
                            if (event.target === this) {
                                ModalImplementation.close();
                            }
                        });

                        // window.addEventListener('keydown', function (event: KeyboardEvent) {
                        //     if (event.key === 'Escape') {
                        //         ModalImplementation.close();
                        //     }
                        // });
                    }


                    // List of accepted MIME types
                    const acceptedFileTypes = [
                        'image/jpeg',
                        'image/png',
                        'application/pdf',
                        'image/gif',
                        'application/msword',
                        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                        'application/vnd.ms-excel',
                        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                        'application/vnd.ms-powerpoint',
                        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
                        'text/csv',
                        'application/zip',
                        'application/x-zip-compressed',
                    ];

                    // Function to validate file type
                    function validateFileType(file) {
                        console.log(file.type);
                        return acceptedFileTypes.includes(file.type);
                    }

                    // Function to handle file input change
                    function handleFileInput(event) {
                        const files = event.target.files;
                        for (let i = 0; i < files.length; i++) {
                            if (!validateFileType(files[i])) {
                                // alert(`Invalid file type: ${files[i].name}`);
                                window.Swal.fire({
                                    title: 'Error',
                                    text: 'Invalid File Type or Corrupted File. Please try again.',
                                    icon: 'error',
                                    confirmButtonText: 'Okay',
                                });
                                event.target.value = ''; // Clear the input
                                break;
                            }
                        }
                    }

                    // Function to attach event listener to file input elements
                    function attachFileInputListener(input) {
                        if (input.tagName === 'INPUT' && input.type === 'file') {
                            input.addEventListener('change', handleFileInput);
                        }
                    }

                    // Function to initialize file input listeners
                    function initializeFileInputListeners() {
                        const fileInputs = document.querySelectorAll('input[type="file"]');
                        fileInputs.forEach((input) => attachFileInputListener(input));
                    }

                    // Initialize listeners for existing file inputs
                    initializeFileInputListeners();

                    // Use MutationObserver to watch for new file input elements
                    const observer = new MutationObserver((mutations) => {
                        mutations.forEach((mutation) => {
                            mutation.addedNodes.forEach((node) => {
                                if (node.nodeType === 1) {
                                    // Ensure it's an element node
                                    // Attach listener to the new file input if found
                                    console.log(node);
                                    if ((node as HTMLElement).tagName === 'INPUT' && (node as HTMLInputElement).type === 'file') {
                                        attachFileInputListener(node);
                                    }

                                    // Check for file inputs within added nodes (e.g., within new elements)
                                    const nestedFileInputs =
                                        (node as HTMLElement).querySelectorAll && (node as HTMLElement).querySelectorAll('input[type="file"]');
                                    nestedFileInputs && nestedFileInputs.forEach((input) => attachFileInputListener(input));
                                }
                            });
                        });
                    });

                    // Start observing the document for changes
                    observer.observe(document.body, { childList: true, subtree: true });


                } else {
                    const modal = new Modal();
                    modal.render();
                    modal.open(type, doc);
                }
            }
        }

        public close(): void {
            const modalElement = document.getElementById(this.currentModalID);
            if (modalElement) {
                modalElement.remove(); // This removes the modal from the DOM
                this.currentModalID = '';
            }
        }
        public render(): void {
            // put modal html into document here
            const modalContainer = document.createElement('div');
            modalContainer.innerHTML = SupportingDocsModal;

            // Append the modal container to the body
            document.body.appendChild(modalContainer);

            // Event Listeners
        }
    }
}